/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { useAuth0 } from '@auth0/auth0-react'
import { t, Trans } from '@lingui/macro'
import PhoneIcon from '@mui/icons-material/Phone'
import { Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import {
    AspenCandidatesService,
    CandidatePatchDTO,
    CarePartnerUpdateDTO,
    CreateCarePartnerModal,
    useCandidate,
    useCarePartnerByCandidate
} from '@om1/aspen-api'
import { useContactAttempts } from '@om1/aspen-api/codegen/hooks/useContactAttempts'
import { usePatientManagers } from '@om1/aspen-api/codegen/hooks/usePatientManagers'
import { formatDate, InlineDropdown, InlineDropdownOption } from '@om1/platform-components'
import { Tag } from 'antd'
import { AlertTriangle, Calendar, CalendarCheck, Check, DownloadIcon, Edit, Loader2, Phone, UserRoundCheck, X } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AdditionalPhoneData, AdditionalPhoneModal } from '../components/AdditionalPhoneModal'
import { AppointmentActions } from '../components/AppointmentActions'
import { CopyableIdentifier } from '../components/CopyableIdentifier'
import EditContactInfoModal from '../components/EditContactInfoModal'
import LoadingComponent from '../components/LoadingComponent'
import { LogAttemptInput } from '../components/LogAttemptInput'
import { LogAttemptTable } from '../components/LogAttemptTable'
import { StatusBadge } from '../components/StatusBadge'
import { SubjectHistory } from '../components/SubjectHistory'
import { useCreateCalendarEvent } from '../hooks/useCreateCalendarEvent'
import { APPOINTMENT_STATUS_COLORS, APPOINTMENT_STATUS_LABELS, appointmentZone, formatAppointmentSlot } from '../utils/appointment'
import { usesSingleAllocatedStatus } from '../utils/candidate-status'
import GenericDetailPage from './GenericDetailPage'

type CandidateStatus =
    | 'candidate'
    | 'in_contact'
    | 'registered'
    | 'allocated'
    | 'allocated_group_2'
    | 'allocated_group_3'
    | 'record_retrieval'
    | 'screen_failure'
    | 'undecided'
    | 'declined'
    | 'unreachable'
    | 'duplicate'

type DeclineReason = 'not_interested' | 'ineligible_group_2' | 'ineligible_group_2_and_3'

const HandleCandidateButtons = ({
    loading,
    updateCandidateStatus,
    status,
    showDeclineReason,
    onDeclineClick,
    onDeclineSelect,
    declineReasonOptions
}: {
    loading: boolean
    updateCandidateStatus: (status: 'allocated' | 'allocated_group_2' | 'allocated_group_3' | 'declined' | 'candidate') => void
    status: CandidateStatus
    showDeclineReason: boolean
    onDeclineClick: () => void
    onDeclineSelect: (reason: DeclineReason) => void
    declineReasonOptions: InlineDropdownOption<DeclineReason | ''>[]
}) => {
    const { user } = useAuth0()
    const singleAllocatedStatus = usesSingleAllocatedStatus(user?.org_name as string | undefined)

    if (loading) {
        return <LoadingComponent />
    }

    if (
        status === 'allocated' ||
        status === 'allocated_group_2' ||
        status === 'allocated_group_3' ||
        status === 'declined' ||
        status === 'record_retrieval' ||
        status === 'duplicate'
    ) {
        return (
            <div className='flex items-center gap-2'>
                <button
                    className='px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm'
                    onClick={() => updateCandidateStatus('candidate')}
                >
                    <Trans>Reset to Candidate</Trans>
                </button>
            </div>
        )
    }

    return (
        <div className='flex gap-2 h-9'>
            {singleAllocatedStatus ? (
                <button
                    className='px-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm'
                    onClick={() => updateCandidateStatus('allocated')}
                    disabled={loading}
                >
                    <Check size={16} className='mr-1.5' />
                    <Trans>Allocate</Trans>
                </button>
            ) : (
                <>
                    <button
                        className='px-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm'
                        onClick={() => updateCandidateStatus('allocated_group_2')}
                        disabled={loading}
                    >
                        <Check size={16} className='mr-1.5' />
                        <Trans>Allocate Group 2</Trans>
                    </button>
                    <button
                        className='px-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm'
                        onClick={() => updateCandidateStatus('allocated_group_3')}
                        disabled={loading}
                    >
                        <Check size={16} className='mr-1.5' />
                        <Trans>Allocate Group 3</Trans>
                    </button>
                </>
            )}
            <button
                className='px-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center text-sm'
                onClick={onDeclineClick}
                disabled={loading}
            >
                <X size={16} className='mr-1.5' />
                <Trans>Decline</Trans>
            </button>
            {showDeclineReason && (
                <InlineDropdown<DeclineReason | ''>
                    value={''}
                    options={declineReasonOptions}
                    onSelect={(reason) => {
                        if (!reason) return
                        onDeclineSelect(reason)
                    }}
                    renderTrigger={(value, isOpen) => (
                        <div className='inline-flex items-center cursor-pointer text-xs text-gray-600 hover:text-gray-800'>
                            <span>{t`Select reason`}</span>
                            <span className={`ml-1 text-[10px] transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                        </div>
                    )}
                />
            )}
        </div>
    )
}

// Styled action button component
const ActionButton = ({
    icon,
    label,
    onClick,
    className = '',
    primary = false,
    disabled = false
}: {
    icon: React.ReactNode
    label: string
    onClick: () => void
    className?: string
    primary?: boolean
    disabled?: boolean
}) => {
    const baseClasses =
        'flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md transition-colors text-sm font-medium min-w-[140px] shadow-sm h-[36px] disabled:opacity-60 disabled:cursor-not-allowed'
    const primaryClasses = 'bg-primary-main text-white hover:bg-primary-dark'
    const secondaryClasses = 'bg-white border border-gray-200 text-primary-main hover:bg-gray-50'

    return (
        <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${primary ? primaryClasses : secondaryClasses} ${className}`}>
            {icon}
            <span>{label}</span>
        </button>
    )
}

// Component to display candidate information in a clean, Apple-style layout
const CandidateInfoSection = ({
    title,
    icon,
    children,
    className = '',
    actionButton,
    titleAction
}: {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
    className?: string
    actionButton?: React.ReactNode
    titleAction?: React.ReactNode
}) => {
    return (
        <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden mb-4 shadow-sm ${className}`}>
            <div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50'>
                <div className='flex items-center gap-2'>
                    <div className='text-primary-main'>{icon}</div>
                    <h3 className='text-base font-semibold text-gray-800 m-0'>{title}</h3>
                </div>
                {titleAction}
            </div>
            <div className='p-4'>
                {actionButton}
                {children}
            </div>
        </div>
    )
}

// Info field component for consistent styling
const InfoField = ({ label, value, status }: { label: string; value: React.ReactNode; status?: CandidateStatus }) => {
    const isPreferredContactTime = label === t`Preferred Contact Time`
    const showWarning = isPreferredContactTime && isDateInPast(value as string) && status === 'candidate'

    return (
        <div className='mb-3 last:mb-0'>
            <p className='text-xs text-gray-500 mb-0.5'>{label}</p>
            <div className='flex items-center gap-2'>
                <p className='text-sm font-medium break-words'>{value || 'N/A'}</p>
                {showWarning && <AlertTriangle size={16} className='text-orange-500' />}
            </div>
        </div>
    )
}

// Format time slot for display in EST
const formatTimeSlot = (startDatetime?: string | null, endDatetime?: string | null) => {
    if (!startDatetime || !endDatetime) return 'N/A'

    const start = new Date(startDatetime)
    const end = new Date(endDatetime)

    // Convert UTC to EST for display
    const startTime = start.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
    })
    const endTime = end.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
    })

    // Map the times to proper time slot labels
    // Get hours in EST timezone
    const startHourEST = start.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        hour12: false
    })
    const endHourEST = end.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        hour12: false
    })

    // Format the date in European format (DD/MM/YY) using the common utility
    // Get date components in EST timezone
    const estYear = start.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric' })
    const estMonth = start.toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit' })
    const estDay = start.toLocaleString('en-US', { timeZone: 'America/New_York', day: '2-digit' })
    // Create a date string in YYYY-MM-DD format and format it using the common utility
    const estDateString = `${estYear}-${estMonth}-${estDay}`
    const dateInEST = formatDate(estDateString)

    // Determine the time slot based on the hours in EST
    if (startHourEST === '9' && endHourEST === '13') {
        return `${dateInEST}, 9:00 AM – 1:00 PM EST`
    } else if (startHourEST === '13' && endHourEST === '17') {
        return `${dateInEST}, 1:00 PM – 5:00 PM EST`
    } else {
        // Fallback to exact times if they don't match standard slots
        return `${dateInEST}, ${startTime} – ${endTime} EST`
    }
}

// Add this helper function near other utility functions
const isDateInPast = (dateString?: string | null) => {
    if (!dateString) return false
    return new Date(dateString) < new Date()
}

const CandidateDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { candidate, updateCandidateStatus, loading, fetchCandidate } = useCandidate(id)
    const { carePartner, loading: carePartnerLoading } = useCarePartnerByCandidate(id)

    // Calendar invite uses the same Google-first flow as the candidate list so
    // the two stay in sync (dashboard link, telemetry, .ics fallback, audit).
    // Refetch on success so any candidate-level changes are reflected.
    const { triggerCreate: triggerCalendarCreate, loading: calendarLoading } = useCreateCalendarEvent({
        candidate,
        onDownloaded: fetchCandidate
    })

    // Fetch patient managers (including inactive for historical records) to pass to SubjectHistory
    // Memoize params to prevent infinite re-renders (usePatientManagers depends on params object reference)
    const patientManagersParams = useMemo(
        () => ({
            page: 1,
            limit: 1000, // Get all patient managers
            active: null, // Include inactive for historical records
            sort: 'name:asc'
        }),
        []
    )
    const { patientManagers } = usePatientManagers(patientManagersParams)

    const {
        contactAttempts,
        isLoading: contactAttemptsLoading,
        error: contactAttemptsError,
        fetchContactAttempts,
        submitAttempt,
        updateAttempt
    } = useContactAttempts({
        entityType: 'candidate',
        entityId: id || ''
    })

    const [editingAttempt, setEditingAttempt] = useState<any>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false)
    const [isUpdatingContact, setIsUpdatingContact] = useState(false)
    const [isAdditionalPhoneModalOpen, setIsAdditionalPhoneModalOpen] = useState(false)
    const [isUpdatingAdditionalPhones, setIsUpdatingAdditionalPhones] = useState(false)
    const [editingPhoneSlot, setEditingPhoneSlot] = useState<'phone1' | 'phone2' | undefined>(undefined)
    const [showDeclineReason, setShowDeclineReason] = useState(false)
    const { user } = useAuth0()

    const declineReasonOptions: InlineDropdownOption<DeclineReason | ''>[] = [
        { value: '', label: t`Select reason`, disabled: true },
        { value: 'not_interested', label: t`Not Interested` },
        { value: 'ineligible_group_2', label: t`Ineligible - Group 2` },
        { value: 'ineligible_group_2_and_3', label: t`Ineligible - Group 2&3` }
    ]

    useEffect(() => {
        if (id) {
            fetchContactAttempts()
        }
    }, [id, fetchContactAttempts])

    const handleOpenModal = () => {
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setEditingAttempt(null)
    }

    const handleSubmitAttempt = async (data: any) => {
        await submitAttempt(data)
        handleCloseModal()
    }

    const handleEditAttempt = (attempt: any) => {
        setEditingAttempt(attempt)
        setIsModalOpen(true)
    }

    const handleUpdateAttempt = async (data: any) => {
        if (!editingAttempt) return
        const success = await updateAttempt({
            contactAttemptId: editingAttempt.id,
            contactType: data.contactType,
            outcome: data.outcome,
            notes: data.notes,
            nextContactTime: data.nextContactTime
        })
        if (success) {
            setEditingAttempt(null)
            handleCloseModal()
        }
    }

    const handleUpdateContactInfo = async (patchData: CandidatePatchDTO | CarePartnerUpdateDTO) => {
        setIsUpdatingContact(true)
        try {
            // Type guard: ensure it's CandidatePatchDTO (which has id field)
            if ('id' in patchData) {
                await AspenCandidatesService.patchCandidatesCandidateIdPatch({
                    candidateId: id!,
                    requestBody: patchData as CandidatePatchDTO
                })
            } else {
                throw new Error('Invalid patch data for candidate')
            }
            // Refresh candidate data
            await fetchCandidate()
            setIsEditContactModalOpen(false)
        } catch (error) {
            console.error('Error updating contact information:', error)
            throw error // Re-throw to let the modal handle the error display
        } finally {
            setIsUpdatingContact(false)
        }
    }

    const handleUpdateAdditionalPhones = async (data: AdditionalPhoneData) => {
        if (!candidate) return

        setIsUpdatingAdditionalPhones(true)
        try {
            const patchData: CandidatePatchDTO = {
                id: candidate.id
            }

            // Update the appropriate slot
            if (data.slot === 'phone1') {
                patchData.additionalPhone1 = data.phone
                patchData.additionalPhone1Label = data.label
            } else {
                patchData.additionalPhone2 = data.phone
                patchData.additionalPhone2Label = data.label
            }

            await AspenCandidatesService.patchCandidatesCandidateIdPatch({
                candidateId: id!,
                requestBody: patchData
            })
            await fetchCandidate()
            setIsAdditionalPhoneModalOpen(false)
        } catch (error) {
            console.error('Error updating additional phone numbers:', error)
            throw error
        } finally {
            setIsUpdatingAdditionalPhones(false)
        }
    }

    const handleCandidateStatusUpdate = async (status: 'allocated' | 'allocated_group_2' | 'allocated_group_3' | 'declined' | 'candidate') => {
        setShowDeclineReason(false)
        await updateCandidateStatus(status)
        await fetchCandidate()
    }

    const handleDeclineReasonSelect = async (reason: DeclineReason) => {
        if (!candidate) return
        await AspenCandidatesService.patchCandidatesCandidateIdPatch({
            candidateId: candidate.id,
            requestBody: {
                id: candidate.id,
                status: 'declined',
                declineReason: reason
            } as CandidatePatchDTO
        })
        setShowDeclineReason(false)
        await fetchCandidate()
    }

    if (!candidate) {
        return (
            <div className='flex flex-col justify-center items-center h-full'>
                <LoadingComponent />
            </div>
        )
    }

    const candidateStatus = candidate.status as CandidateStatus
    // Rendered in the patient's zone, deliberately — see utils/appointment.
    const appointment = candidate.appointment
    const appointmentSlot = appointment ? formatAppointmentSlot(appointment, candidate.timezone) : null

    return (
        <>
            <GenericDetailPage
                backButtonText={t`To Candidates`}
                title={
                    <div className='flex items-center gap-3'>
                        <div className='bg-primary-main text-white p-1.5 rounded-full flex-shrink-0'>
                            <UserRoundCheck size={20} />
                        </div>
                        <div className='min-w-0'>
                            <Typography variant='h6' className='font-semibold truncate'>
                                {`${candidate.firstName} ${candidate.lastName}`}
                            </Typography>
                            <div className='flex items-center gap-2 mt-1'>
                                <CopyableIdentifier identifier={candidate.friendlyId} />
                                <span className='text-xs text-gray-500'>ID: {candidate.id}</span>
                            </div>
                        </div>
                    </div>
                }
                data={candidate}
                columns={[]}
                backRoute='/candidates'
                actionButtons={[
                    <div key='approve-reject-candidate' className='flex justify-end gap-2 flex-wrap'>
                        <ActionButton
                            icon={calendarLoading ? <Loader2 className='w-4 h-4 animate-spin' /> : <DownloadIcon className='w-4 h-4' />}
                            label={calendarLoading ? t`Creating…` : t`Calendar Invite`}
                            onClick={triggerCalendarCreate}
                            disabled={calendarLoading}
                        />
                        <HandleCandidateButtons
                            loading={loading}
                            updateCandidateStatus={handleCandidateStatusUpdate}
                            status={candidateStatus}
                            showDeclineReason={showDeclineReason}
                            onDeclineClick={() => setShowDeclineReason(true)}
                            onDeclineSelect={handleDeclineReasonSelect}
                            declineReasonOptions={declineReasonOptions}
                        />
                    </div>
                ]}
                additionalComponents={[
                    <div key='content-layout' className='grid grid-cols-1 md:grid-cols-12 gap-4 pb-16 min-h-0'>
                        <div className='md:col-span-8 space-y-4 min-h-0'>
                            <CandidateInfoSection
                                title='Contact Information'
                                icon={<UserRoundCheck size={18} />}
                                titleAction={
                                    <div className='flex gap-2'>
                                        {/* Only show "Add Additional Phone" button if there's at least one slot available */}
                                        {(!candidate.additionalPhone1 || !candidate.additionalPhone2) && (
                                            <Button
                                                color='primary'
                                                onClick={() => {
                                                    setEditingPhoneSlot(undefined)
                                                    setIsAdditionalPhoneModalOpen(true)
                                                }}
                                                size='small'
                                                variant='outlined'
                                                className='h-8'
                                                startIcon={<Phone size={16} />}
                                            >
                                                {t`Add Additional Phone`}
                                            </Button>
                                        )}
                                        <Button
                                            color='primary'
                                            onClick={() => setIsEditContactModalOpen(true)}
                                            size='small'
                                            variant='outlined'
                                            className='h-8'
                                            startIcon={<Edit size={16} />}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                }
                            >
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                    <InfoField label={t`Email`} value={candidate.email} />
                                    <InfoField label={t`Phone`} value={candidate.phone} />
                                    {(candidate.additionalPhone1 || candidate.additionalPhone2) && (
                                        <>
                                            {candidate.additionalPhone1 && (
                                                <div className='mb-3 last:mb-0'>
                                                    <div className='flex items-center gap-2 mb-0.5'>
                                                        <p className='text-xs text-gray-500'>
                                                            {candidate.additionalPhone1Label
                                                                ? `${candidate.additionalPhone1Label} Phone`
                                                                : t`Additional Phone 1`}
                                                        </p>
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditingPhoneSlot('phone1')
                                                                setIsAdditionalPhoneModalOpen(true)
                                                            }}
                                                            size='small'
                                                            className='h-6 w-6 p-0'
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <Edit size={16} />
                                                        </IconButton>
                                                    </div>
                                                    <p className='text-sm font-medium'>{candidate.additionalPhone1 || 'N/A'}</p>
                                                </div>
                                            )}
                                            {candidate.additionalPhone2 && (
                                                <div className='mb-3 last:mb-0'>
                                                    <div className='flex items-center gap-2 mb-0.5'>
                                                        <p className='text-xs text-gray-500'>
                                                            {candidate.additionalPhone2Label
                                                                ? `${candidate.additionalPhone2Label} Phone`
                                                                : t`Additional Phone 2`}
                                                        </p>
                                                        <IconButton
                                                            onClick={() => {
                                                                setEditingPhoneSlot('phone2')
                                                                setIsAdditionalPhoneModalOpen(true)
                                                            }}
                                                            size='small'
                                                            className='h-6 w-6 p-0'
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <Edit size={16} />
                                                        </IconButton>
                                                    </div>
                                                    <p className='text-sm font-medium'>{candidate.additionalPhone2 || 'N/A'}</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <InfoField label={t`Date of Birth`} value={formatDate(candidate.dateOfBirth)} />
                                    {/* The legacy preference is shown only when there is no booked
                                        appointment to supersede it. Showing both invites a coordinator
                                        to ring at the preferred window instead of the actual slot, and
                                        the past-date warning on this field would fire misleadingly for
                                        a candidate whose real appointment is still in the future. */}
                                    {!appointment && (
                                        <InfoField
                                            label={t`Preferred Contact Time`}
                                            value={formatTimeSlot(candidate.preferredContactStartDatetime, candidate.preferredContactEndDatetime)}
                                            status={candidateStatus}
                                        />
                                    )}
                                    <InfoField label={t`SMS Consent`} value={candidate.smsConsent ? 'Yes' : 'No'} />
                                </div>
                            </CandidateInfoSection>

                            {appointment && (
                                <CandidateInfoSection title='Appointment' icon={<CalendarCheck size={18} />}>
                                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                        <div>
                                            <p className='text-xs text-gray-500 mb-1'>Scheduled</p>
                                            <p className='text-sm font-medium'>
                                                {appointmentSlot ?? 'Unknown'}{' '}
                                                <Tag color={APPOINTMENT_STATUS_COLORS[appointment.status]} className='ml-1'>
                                                    {APPOINTMENT_STATUS_LABELS[appointment.status]}
                                                </Tag>
                                            </p>
                                            {/* The zone is the patient's, not the coordinator's — see utils/appointment. */}
                                            <p className='text-xs text-gray-500 mt-1'>
                                                Patient timezone: {appointmentZone(appointment, candidate.timezone)}
                                            </p>
                                        </div>
                                        <InfoField label={t`Patient Manager`} value={appointment.hostEmail} />
                                        {appointment.meetingUrl && (
                                            <div>
                                                <p className='text-xs text-gray-500 mb-1'>Meeting link</p>
                                                <a
                                                    href={appointment.meetingUrl}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='text-sm font-medium text-blue-600 hover:underline break-all'
                                                >
                                                    {appointment.meetingUrl}
                                                </a>
                                            </div>
                                        )}
                                        <div>
                                            <p className='text-xs text-gray-500 mb-1'>Actions</p>
                                            <AppointmentActions
                                                candidate={{ ...candidate, appointment }}
                                                study={(user?.org_name as string | undefined) ?? ''}
                                                onChanged={() => fetchCandidate()}
                                            />
                                        </div>
                                    </div>
                                </CandidateInfoSection>
                            )}

                            <CandidateInfoSection title='Study Information' icon={<Calendar size={18} />}>
                                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                                    <InfoField label={t`Study Name`} value={user?.org_name} />
                                    <InfoField label={t`Referrer ID`} value={candidate.referrerId} />
                                    <InfoField label={t`Created At`} value={formatDate(candidate.createdDttm)} />
                                    <InfoField label={t`Updated At`} value={formatDate(candidate.updatedDttm)} />
                                </div>
                            </CandidateInfoSection>

                            <CandidateInfoSection
                                title='Contact Attempts'
                                icon={<PhoneIcon className='text-primary-main h-5 w-5' />}
                                titleAction={
                                    <Button
                                        color='primary'
                                        onClick={handleOpenModal}
                                        startIcon={<span>{t`+`}</span>}
                                        className='bg-primary-main text-white hover:bg-gray-200 rounded-md h-8 mb-4'
                                        size='small'
                                    >
                                        {t`Add New Attempt`}
                                    </Button>
                                }
                            >
                                {contactAttemptsLoading ? (
                                    <LoadingComponent />
                                ) : (
                                    <div className='h-[200px] overflow-visible'>
                                        <LogAttemptTable attempts={contactAttempts} onEdit={handleEditAttempt} />
                                    </div>
                                )}
                            </CandidateInfoSection>

                            <SubjectHistory
                                subjectType='candidate'
                                subjectId={id || ''}
                                onUpdate={fetchCandidate}
                                patientManagers={patientManagers}
                            />
                        </div>

                        <div className='md:col-span-4 min-h-0'>
                            <CandidateInfoSection title='Additional Details' icon={<UserRoundCheck size={18} />}>
                                <div className='space-y-3'>
                                    <InfoField label={t`First Name`} value={candidate.firstName} />
                                    <InfoField label={t`Last Name`} value={candidate.lastName} />
                                    <div className='mb-3 last:mb-0'>
                                        <p className='text-xs text-gray-500 mb-0.5'>{t`Status`}</p>
                                        <StatusBadge status={candidateStatus} />
                                    </div>
                                </div>
                            </CandidateInfoSection>

                            <CandidateInfoSection
                                title='Care Partner Details'
                                icon={<UserRoundCheck size={18} />}
                                titleAction={
                                    carePartner && (
                                        <Button
                                            color='primary'
                                            onClick={() => setIsEditModalOpen(true)}
                                            size='small'
                                            variant='outlined'
                                            className='h-8'
                                        >
                                            Edit
                                        </Button>
                                    )
                                }
                            >
                                {carePartnerLoading ? (
                                    <div className='flex items-center justify-center py-4'>
                                        <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary-main'></div>
                                    </div>
                                ) : carePartner ? (
                                    <div className='space-y-3'>
                                        <InfoField label={t`Care Partner Name`} value={`${carePartner.firstName} ${carePartner.lastName}`} />
                                        <InfoField label={t`Phone Number`} value={carePartner.phoneNumber} />
                                        {carePartner.email && <InfoField label={t`Email Address`} value={carePartner.email} />}
                                        <InfoField label={t`Created`} value={formatDate(carePartner.createdDttm)} />
                                    </div>
                                ) : (
                                    <div className='text-center py-4'>
                                        <p className='text-sm text-gray-500 mb-2'>No care partner assigned</p>
                                        <p className='text-xs text-gray-400'>
                                            Care partners provide support and can be contacted regarding this candidate
                                        </p>
                                    </div>
                                )}
                            </CandidateInfoSection>
                        </div>
                    </div>
                ]}
            />

            {/* Modal for adding/editing contact attempt */}
            <Dialog
                open={isModalOpen}
                onClose={handleCloseModal}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        width: '500px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogContent sx={{ p: 3 }}>
                    <div className='flex justify-center items-center min-h-[50vh]'>
                        <div className='w-full'>
                            <LogAttemptInput
                                entityName={`${candidate.firstName} ${candidate.lastName}`}
                                onSubmit={editingAttempt ? handleUpdateAttempt : handleSubmitAttempt}
                                onCancel={handleCloseModal}
                                entityType='candidate'
                                entityId={candidate.id}
                                contactAttempts={contactAttempts}
                                isLoading={contactAttemptsLoading}
                                error={contactAttemptsError}
                                initialValues={
                                    editingAttempt
                                        ? {
                                              contactType: editingAttempt.contactType,
                                              outcome: editingAttempt.attemptOutcome,
                                              notes: editingAttempt.notes,
                                              nextContactTime: editingAttempt.suggestedNextContactTime
                                                  ? new Date(editingAttempt.suggestedNextContactTime)
                                                  : null
                                          }
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal for editing care partner */}
            <CreateCarePartnerModal
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSuccess={(carePartnerId: string) => {
                    // Refresh care partner data after edit
                    // This will trigger a refetch of the care partner data
                }}
                mode='edit'
                carePartner={carePartner}
                candidateId={id}
            />

            {/* Modal for editing contact information */}
            <EditContactInfoModal
                open={isEditContactModalOpen}
                onClose={() => setIsEditContactModalOpen(false)}
                onSave={handleUpdateContactInfo}
                candidate={candidate}
                loading={isUpdatingContact}
            />

            <AdditionalPhoneModal
                open={isAdditionalPhoneModalOpen}
                onClose={() => {
                    setIsAdditionalPhoneModalOpen(false)
                    setEditingPhoneSlot(undefined)
                }}
                onSave={handleUpdateAdditionalPhones}
                initialData={{
                    phone1: candidate?.additionalPhone1 || null,
                    phone1Label: candidate?.additionalPhone1Label || null,
                    phone2: candidate?.additionalPhone2 || null,
                    phone2Label: candidate?.additionalPhone2Label || null
                }}
                loading={isUpdatingAdditionalPhones}
                entityType='candidate'
                editingSlot={editingPhoneSlot}
            />
        </>
    )
}

export default CandidateDetailsPage
